import React, { useEffect, useRef, useState } from 'react';
import { CgPerformance } from 'react-icons/cg';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { useAskVantage } from '../../context/AskVantageProvider';
import { useTheme } from '../../theme';
import type { ThemeConfig } from '../../theme';
import TokenPlansModal from '../TokenPlansModal';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';

const UsageReportPopover: React.FC = () => {
  const { quota, messages } = useAskVantage();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showTokenPlansModal, setShowTokenPlansModal] = useState(false);
  const { demo_account: isDemoAccount } = useSelector(
    (state: RootState) => state.company.company_details
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isMobile = useMedia('(max-width: 768px)');

  // Determine popover position based on view and device
  const getPopoverPosition = (): 'top' | 'bottom' => {
    const isMainView = messages.length === 0;
    const isDesktop = !isMobile;

    // Only show at bottom for main view on desktop
    if (isMainView && isDesktop) {
      return 'bottom';
    }

    // All other cases: show at top
    // - Chat view on desktop
    // - Main view on mobile
    // - Chat view on mobile
    return 'top';
  };

  const position = getPopoverPosition();

  // Close popover when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        triggerRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleBuyTokensClick = () => {
    setShowTokenPlansModal(true);
    setIsOpen(false);
  };

  // Use new backend field names
  const dailyFreeUsage = quota.current_usage?.daily_free_usage || 0;
  const dailyFreeQuota = quota.current_usage?.daily_free_quota || 0;
  const dailyFreeRemaining = quota.current_usage?.daily_free_remaining || 0;
  const purchasedCredits = quota.current_usage?.remaining_purchased_tokens || 0;
  const isDailyQuotaExhausted = quota.current_usage?.is_daily_quota_exhausted || false;

  // Grey out daily section ONLY if quota exhausted AND purchased credits available
  const shouldGreyOutDailyUsage = isDailyQuotaExhausted && purchasedCredits > 0;
  const hasPurchasedCredits = purchasedCredits > 0;

  const getDailyProgressColor = () => {
    const dailyPercentage = quota.current_usage?.daily_free_usage_percent || 0;

    // If daily quota exhausted AND purchased credits available, show muted grey
    if (shouldGreyOutDailyUsage) {
      return '#94a3b8'; // Muted grey-blue color
    }

    // Otherwise, show normal status colors
    if (dailyPercentage >= 100) return '#ef4444';
    if (dailyPercentage >= 90) return '#f59e0b';
    return '#10b981';
  };

  const formatNumberFull = (num: number) => {
    return num.toLocaleString();
  };

  const formatNumberAbbreviated = (num: number) => {
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`;
    } else if (num >= 1_000) {
      return `${(num / 1_000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const getResetTimeText = () => {
    const resetsAt = quota.current_usage?.daily_quota_resets_at;
    if (!resetsAt) {
      return 'Daily quota resets at midnight UTC';
    }

    const resetDate = new Date(resetsAt);
    const localResetTime = resetDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `Daily quota resets at ${localResetTime}`;
  };

  if (quota.loading) {
    return null;
  }

  return (
    <>
      <PopoverContainer>
        <IconButton
          ref={triggerRef}
          onClick={handleToggle}
          $theme={theme}
          aria-label="Usage Report"
          type="button"
          $isActive={isOpen}
        >
          <CgPerformance size={16} />
        </IconButton>

        {isOpen && (
          <PopoverContent ref={popoverRef} position={position} $theme={theme}>
            <PopoverHeader $theme={theme}>
              <div className="heading">USAGE REPORT</div>
            </PopoverHeader>

            <div className="sub-content">
              {/* Composite Usage Section */}
              <UsageSection>
                {/* Daily Quota Progress - Greyed out only if exhausted AND credits available */}
                <DailyQuotaSection $isGreyedOut={shouldGreyOutDailyUsage}>
                  <QuotaHeader $theme={theme}>
                    <QuotaTitle $theme={theme}>Daily Free Usage</QuotaTitle>
                    <QuotaValue $theme={theme}>
                      {formatNumberAbbreviated(dailyFreeUsage)} /{' '}
                      {formatNumberAbbreviated(dailyFreeQuota)}
                    </QuotaValue>
                  </QuotaHeader>

                  <CompositeProgressBar $theme={theme}>
                    {/* Used portion */}
                    <ProgressSegment
                      width={(dailyFreeUsage / dailyFreeQuota) * 100}
                      color={getDailyProgressColor()}
                      $theme={theme}
                    />
                    {/* Remaining portion (transparent) */}
                    <ProgressSegment
                      width={(dailyFreeRemaining / dailyFreeQuota) * 100}
                      color="transparent"
                      $theme={theme}
                    />
                  </CompositeProgressBar>

                  {shouldGreyOutDailyUsage && (
                    <InfoText $theme={theme}>
                      Daily free quota exhausted. Using purchased credits now.
                    </InfoText>
                  )}
                </DailyQuotaSection>

                {/* Reset Timer */}
                <ResetInfo $theme={theme}>{getResetTimeText()}</ResetInfo>

                {/* Purchased Credits Section - Below daily usage */}
                {hasPurchasedCredits && (
                  <>
                    <Divider $theme={theme} />
                    <CreditsSection $theme={theme} $isActive={shouldGreyOutDailyUsage}>
                      <CreditsValue $theme={theme}>
                        +{formatNumberFull(purchasedCredits)} tokens available
                      </CreditsValue>
                    </CreditsSection>
                  </>
                )}
              </UsageSection>

              {/* Buy More Tokens Button */}
              {!isDemoAccount && (
                <BuyTokensButton onClick={handleBuyTokensClick} $theme={theme}>
                  Buy More Tokens
                </BuyTokensButton>
              )}
            </div>

            <PopoverArrow position={position} $theme={theme} />
          </PopoverContent>
        )}
      </PopoverContainer>

      <TokenPlansModal show={showTokenPlansModal} onClose={() => setShowTokenPlansModal(false)} />
    </>
  );
};

const PopoverContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const IconButton = styled.button<{ $theme: ThemeConfig; $isActive: boolean }>`
  position: relative;
  background: linear-gradient(135deg, #6ebe7f 0%, #4a9960 100%);
  border: none;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 200ms ease;
  color: white;
  box-shadow:
    0 2px 8px rgba(74, 153, 96, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  overflow: hidden;

  /* Glass reflection effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    border-radius: 50% 50% 0 0;
    pointer-events: none;
  }

  /* Additional highlight */
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 6px;
    width: 8px;
    height: 8px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    filter: blur(2px);
    pointer-events: none;
  }

  svg {
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));
  }

  &:hover {
    transform: scale(1.05);
    box-shadow:
      0 4px 12px rgba(74, 153, 96, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  ${(props) =>
    props.$isActive &&
    `
    background: linear-gradient(135deg, #5dad6f 0%, #3a8950 100%);
    box-shadow:
      0 2px 8px rgba(74, 153, 96, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  `}
`;

const PopoverContent = styled.div<{
  position: 'top' | 'bottom';
  $theme: ThemeConfig;
}>`
  position: absolute;
  ${(props) => (props.position === 'top' ? 'bottom: calc(100% + 8px);' : 'top: calc(100% + 8px);')}
  right: 0;
  width: 280px;
  height: auto;
  background: ${(props) => props.$theme.colors.messageBoxBg};
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: 8px;
  box-shadow:
    0 10px 25px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: popoverAppear 0.2s ease-out;
  .sub-content {
    padding: 8px 16px;
  }

  @keyframes popoverAppear {
    from {
      opacity: 0;
      transform: translateY(${(props) => (props.position === 'top' ? '8px' : '-8px')});
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PopoverArrow = styled.div<{
  position: 'top' | 'bottom';
  $theme: ThemeConfig;
}>`
  position: absolute;
  ${(props) => (props.position === 'top' ? 'top: 100%;' : 'bottom: 100%;')}
  right: 8px;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  ${(props) =>
    props.position === 'top'
      ? `border-top-color: ${props.$theme.colors.messageBoxBg};`
      : `border-bottom-color: ${props.$theme.colors.messageBoxBg};`}
`;

const PopoverHeader = styled.h3<{ $theme: ThemeConfig }>`
  margin: 0 0 10px 0;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${(props) => props.$theme.colors.mainText};
  text-align: left;
  padding: 8px;
  background: ${(props) =>
    props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 8px 8px 0px 0px;
  .heading {
    font-size: 16px !important;
    text-align: center;
  }
`;

const UsageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DailyQuotaSection = styled.div<{ $isGreyedOut: boolean }>`
  opacity: ${(props) => (props.$isGreyedOut ? 0.5 : 1)};
  transition: opacity 0.3s ease;
`;

const QuotaHeader = styled.div<{ $theme: ThemeConfig }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const QuotaTitle = styled.div<{ $theme: ThemeConfig }>`
  font-size: 10px;
  font-weight: 600;
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 0.9;
  white-space: nowrap;
  flex-shrink: 0;
`;

const QuotaValue = styled.div<{ $theme: ThemeConfig }>`
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => props.$theme.colors.mainText};
  white-space: nowrap;
  flex-shrink: 0;
  text-align: right;
`;

const CompositeProgressBar = styled.div<{ $theme: ThemeConfig }>`
  width: 100%;
  height: 20px;
  background: ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ProgressSegment = styled.div<{
  width: number;
  color: string;
  $theme: ThemeConfig;
}>`
  height: 100%;
  width: ${(props) => Math.min(props.width, 100)}%;
  background: ${(props) => props.color};
  transition: all 0.3s ease;
  position: relative;

  ${(props) =>
    props.color !== 'transparent' &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 50%;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.2) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  `}
`;

const InfoText = styled.div<{ $theme: ThemeConfig }>`
  font-size: 10px;
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 0.75;
  margin-top: 6px;
  font-style: normal;
  line-height: 1.4;
`;

const Divider = styled.div<{ $theme: ThemeConfig }>`
  height: 1px;
  background: ${(props) => props.$theme.colors.messageBoxBorder};
  margin: 8px 0;
  opacity: 0.5;
`;

const CreditsSection = styled.div<{ $theme: ThemeConfig; $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props.$isActive ? '10px' : '8px')};
  background: ${(props) =>
    props.$isActive
      ? props.$theme.mode === 'dark'
        ? 'rgba(16, 185, 129, 0.15)'
        : 'rgba(16, 185, 129, 0.1)'
      : props.$theme.mode === 'dark'
        ? 'rgba(59, 130, 246, 0.1)'
        : 'rgba(59, 130, 246, 0.05)'};
  border-radius: 6px;
  border: ${(props) => {
    if (props.$isActive) {
      const color =
        props.$theme.mode === 'dark' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(16, 185, 129, 0.3)';
      return `2px solid ${color}`;
    }
    const color =
      props.$theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)';
    return `1px solid ${color}`;
  }};
  box-shadow: ${(props) => (props.$isActive ? '0 2px 8px rgba(16, 185, 129, 0.2)' : 'none')};
  transition: all 0.3s ease;
`;

const CreditsValue = styled.div<{ $theme: ThemeConfig }>`
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
`;

const ResetInfo = styled.div<{ $theme: ThemeConfig }>`
  font-size: 9px;
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 0.6;
  text-align: center;
  margin-top: 4px;
`;

const BuyTokensButton = styled.button<{ $theme: ThemeConfig }>`
  width: 100%;
  margin-top: 16px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #6ebe7f 0%, #4a9960 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(74, 153, 96, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(74, 153, 96, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default UsageReportPopover;
